<template>
    <a-config-provider :locale="currentLocale">
        <div v-if="!loading">
            <a-card v-if="!!lotesInfo" size="small" title="Informações do lote" style="margin:15px 30px; border-color:#878787;">
                <a-descriptions bordered :column="1" size="small">
                    <a-descriptions-item v-for="(item, label) in lotesInfo" :label="formatLabel(label)" :label-style="{width:'40%'}">
                        <div v-if="!editingLote">
                            <span>{{ formatFieldValue(item, lotesFieldsTypes[label]) }}</span>
                        </div>
                        <span v-else>
                            <a-date-picker
                                v-if="getFieldComponentType(label, lotesFieldsTypes[label]) === 'date'"
                                v-model:value="lotesInfo[label]"
                                :locale="datePickerLocale"
                                format="DD/MM/YYYY"
                                value-format="YYYY-MM-DD"
                                style="width: 100%"
                                placeholder="Selecione a data"
                            />
                            <a-switch
                                v-else-if="getFieldComponentType(label, lotesFieldsTypes[label]) === 'switch'"
                                v-model:checked="lotesInfo[label]"
                                checked-children="Sim"
                                un-checked-children="Não"
                            />
                            <a-textarea
                                v-else-if="getFieldComponentType(label, lotesFieldsTypes[label]) === 'textarea'"
                                v-model:value="lotesInfo[label]"
                                :auto-size="{ minRows: 2, maxRows: 4 }"
                            />
                            <a-input-number
                                v-else-if="getFieldComponentType(label, lotesFieldsTypes[label]) === 'number'"
                                v-model:value="lotesInfo[label]"
                                style="width: 100%"
                                :precision="lotesFieldsTypes[label] === 'double precision' ? 10 : 0"
                            />
                            <a-input
                                v-else
                                v-model:value="lotesInfo[label]"
                            />
                        </span>
                    </a-descriptions-item>
                </a-descriptions>
                <div class="w-full d-flex justify-content-end gap-2 mt-2">
                    <a-button v-if="editingLote" @click="cancelEditing()">Cancelar</a-button>
                    <a-button v-if="editPermission" :type="editingLote ? 'primary' : 'default'" @click="editingLote ? saveLote() : (editingLote = true)">
                        {{ editingLote ? 'Salvar' : 'Editar' }}
                    </a-button>               
                </div>
            </a-card>

            <a-card v-if="!!constructionsInfo" size="small" title="Informações das construções" style="margin:15px 30px; border-color:#878787;">
                <div v-for="construction in constructionsInfo" style="margin-top: 30px">
                    <div class="construction-title">Construção {{ construction.j39_idcons }}</div>

                    <a-descriptions bordered :column="1" size="small">
                        <a-descriptions-item v-for="(item, label) in construction" :label="formatLabel(label)" :label-style="{width:'40%'}">
                            <div v-if="!editingConstructions">
                                <span>{{ formatFieldValue(item, constructionsFieldsTypes[label]) }}</span>
                            </div>
                            <span v-else>
                                <a-date-picker
                                    v-if="getFieldComponentType(label, constructionsFieldsTypes[label]) === 'date'"
                                    v-model:value="construction[label]"
                                    :locale="datePickerLocale"
                                    format="DD/MM/YYYY"
                                    value-format="YYYY-MM-DD"
                                    style="width: 100%"
                                    placeholder="Selecione a data"
                                />
                                <a-switch
                                    v-else-if="getFieldComponentType(label, constructionsFieldsTypes[label]) === 'switch'"
                                    v-model:checked="construction[label]"
                                    checked-children="Sim"
                                    un-checked-children="Não"
                                />
                                <a-textarea
                                    v-else-if="getFieldComponentType(label, constructionsFieldsTypes[label]) === 'textarea'"
                                    v-model:value="construction[label]"
                                    :auto-size="{ minRows: 2, maxRows: 4 }"
                                />
                                <a-input-number
                                    v-else-if="getFieldComponentType(label, constructionsFieldsTypes[label]) === 'number'"
                                    v-model:value="construction[label]"
                                    style="width: 100%"
                                    :precision="constructionsFieldsTypes[label] === 'double precision' ? 10 : 0"
                                />
                                <a-input
                                    v-else
                                    :disabled="label === 'j39_idcons'"
                                    v-model:value="construction[label]"
                                />
                            </span>
                        </a-descriptions-item>
                    </a-descriptions>
                </div>
                <div class="w-full d-flex justify-content-end gap-2 mt-2">
                    <a-button v-if="editingConstructions" @click="cancelEditingConstructions()">Cancelar</a-button>
                    <a-button v-if="editPermission" :type="editingConstructions ? 'primary' : 'default'" @click="editingConstructions ? saveConstructions() : (editingConstructions = true)">
                        {{ editingConstructions ? 'Salvar' : 'Editar' }}
                    </a-button>
                </div>
            </a-card>
        </div>
    </a-config-provider>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import ptBR from 'ant-design-vue/es/locale/pt_BR';
import datePickerPtBR from 'ant-design-vue/es/date-picker/locale/pt_BR';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { openNotification } from '../../../../../../utils/utilities.js';
import verifySpecificFuncPermissions from '../../../../../../utils/verifySpecificFuncPermissions.js';

const props = defineProps(['loteData', 'constructionsData', 'propertyId']);
const currentLocale = ref(ptBR);
const datePickerLocale = datePickerPtBR;
dayjs.locale('pt-br');

const lotesInfo = ref({});
const lotesFieldsTypes = ref({});
const constructionsInfo = ref([]);
const constructionsFieldsTypes = ref({});
const loading = ref(true);
const editPermission = ref(false);
const editingLote = ref(false);
const editingConstructions = ref(false);

onMounted(async () => {
    lotesInfo.value = props.loteData?.data;
    lotesFieldsTypes.value = props.loteData?.types;

    constructionsInfo.value = props.constructionsData?.data;
    constructionsFieldsTypes.value = props.constructionsData?.types;

    editPermission.value = await verifySpecificFuncPermissions(5)

    loading.value = false;
})

const loadInfo = async () => {
    const lotesData = (await window.axios.get('/api/map-actions/bic/' + props.propertyId + '/lot')).data.data;
    lotesInfo.value = lotesData.data;
    lotesFieldsTypes.value = lotesData.types;

    const constructionsData = (await window.axios.get('/api/map-actions/bic/' + props.propertyId + '/constructions')).data.data;
    constructionsInfo.value = constructionsData.data;
    constructionsFieldsTypes.value = constructionsData.types;
}

const formatLabel = (label) => {
    const formattedLabel = label.replace(/_/g, ' ');

    return formattedLabel.charAt(0).toUpperCase() + formattedLabel.slice(1);
}

const formatFieldValue = (value, type) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }

    if (type === 'boolean') {
        return value ? 'Sim' : 'Não';
    }

    if (type === 'date') {
        const parsedDate = dayjs(value);
        return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : value;
    }

    return value;
}

const getFieldComponentType = (label, type) => {
    switch (type) {
        case 'boolean':
            return 'switch';
        case 'integer':
        case 'double precision':
        case 'numeric':
        case 'real':
        case 'bigint':
        case 'smallint':
            return 'number';
        case 'date':
            return 'date';
        case 'text':
            return 'textarea';
        case 'character varying':
        default:
            return 'input';
    }
}

const saveLote = async () => {
    await window.axios.put('/api/map-actions/bic/' + props.propertyId + '/lot', lotesInfo.value);
    editingLote.value = false;

    await loadInfo();
    openNotification('success', 'Informações do lote salvas com sucesso!');
}

const saveConstructions = async () => {
    for (const construction of constructionsInfo.value) {
        await window.axios.put('/api/map-actions/bic/' + props.propertyId + '/constructions', construction);
    }
    editingConstructions.value = false;

    await loadInfo();
    openNotification('success', 'Informações das construções salvas com sucesso!');
}

const cancelEditing = async () => {
    editingLote.value = false;
    await loadInfo();
}

const cancelEditingConstructions = async () => {
    editingConstructions.value = false;
    await loadInfo();
}
</script>

<style scoped>
.center-flex {
    display: flex;
    justify-content: center;
    align-items: center;
}

.construction-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
}
</style>
